import { PostPurchaseSplitStep } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { useTranslation } from "@redotech/redo-i18n";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { PartialWithRequiredType } from "..";
import {
  SplitVariantControls,
  SplitVariantControlsProps,
} from "../common/split-variant-controls";

interface PostPurchaseSplitViewInspectorProps {
  step: PartialWithRequiredType<PostPurchaseSplitStep>;
  onClose?: () => void;
}

export const PostPurchaseSplitViewInspector = memo(
  function PostPurchaseSplitViewInspector({
    step,
    onClose,
  }: PostPurchaseSplitViewInspectorProps) {
    const { t } = useTranslation();
    const treatments = useMemo(() => {
      return step.treatments ?? [];
    }, [step.treatments]);

    const totalWeight = useMemo(() => {
      return treatments.reduce(
        (acc, treatment) => acc + (treatment.weight || 0),
        0,
      );
    }, [treatments]);

    const isWeightValid: SplitVariantControlsProps["isWeightValid"] =
      useMemo(() => {
        const firstTreatment = treatments[0];
        if (!firstTreatment) {
          return { isValid: false, errorMessage: t("No variants found") };
        }

        const allTreatmentsHaveWeight = treatments.every(
          (treatment) =>
            treatment.weight && treatment.weight > 0 && treatment.weight <= 100,
        );
        if (!allTreatmentsHaveWeight) {
          return {
            isValid: false,
            errorMessage: t(
              "All variants must have a weight between 0% and 100%",
            ),
          };
        }

        const totalWeightIs100 = totalWeight === 100;
        if (totalWeightIs100) {
          return { isValid: true };
        }

        const allTreatmentsHaveSameWeight = treatments.every(
          (treatment) =>
            treatment.weight && treatment.weight === firstTreatment.weight,
        );
        if (allTreatmentsHaveSameWeight) {
          return { isValid: true };
        } else {
          return {
            isValid: false,
            errorMessage: t(
              "All variants must add up to 100% (or have the same weight)",
            ),
          };
        }
      }, [totalWeight, treatments, t]);

    return (
      <Flex dir="column" gap="3xl" justify="flex-start">
        <Flex dir="row" justify="space-between">
          <Text fontSize="md" fontWeight="semibold">
            {t("A/B test")}
          </Text>
          <RedoButton
            aria-label={t("Close")}
            hierarchy="tertiary"
            IconLeading={XIcon}
            onClick={onClose}
            size="sm"
          />
        </Flex>
        <Flex dir="column" gap="xl" justify="flex-start">
          <Flex dir="column" gap="xs" justify="flex-start">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              {t("Test name")}
            </Text>
            <Text fontSize="xs" fontWeight="medium" textColor="primary">
              {step.name ?? ""}
            </Text>
          </Flex>
          <Flex dir="column" gap="xs" justify="flex-start">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              {t("Hypothesis")}
            </Text>
            <Text fontSize="xs" fontWeight="medium" textColor="primary">
              {step.description ?? ""}
            </Text>
          </Flex>
          <Flex dir="column" gap="xs" justify="flex-start">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              {t("Variants")}
            </Text>
            <SplitVariantControls
              isWeightValid={isWeightValid}
              onAddVariant={() => {}}
              onBalanceWeights={() => {}}
              onRemoveVariant={() => {}}
              onVariantNameChange={() => {}}
              onVariantWeightChange={() => {}}
              readOnly
              totalWeight={totalWeight}
              variants={treatments.map((treatment) => ({
                id: treatment.treatmentId ?? "",
                name: treatment.name ?? "",
                weight: treatment.weight ?? 0,
              }))}
            />
          </Flex>
        </Flex>
      </Flex>
    );
  },
);
